import React, { useState } from 'react';
import { 
  MoreVertical, Edit3, Trash2, Copy, Eye, Power, AlertTriangle
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from './DropdownMenu';
import { Button } from './Button';

export interface CustomRowAction {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
}

export interface RowActionsProps {
  onViewDetails?: () => void;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onToggleStatus?: () => void;
  isStatusActive?: boolean;
  statusToggleLabel?: string;
  onDelete?: () => void;
  deleteTitle?: string;
  deleteMessage?: string;
  entityName?: string;
  customActions?: CustomRowAction[];
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
}

export function RowActions({
  onViewDetails,
  onEdit,
  onDuplicate,
  onToggleStatus,
  isStatusActive = true,
  statusToggleLabel,
  onDelete,
  deleteTitle = 'Delete Confirmation',
  deleteMessage,
  entityName = 'this item',
  customActions = [],
  disabled = false,
  readOnly = false,
  className = '',
}: RowActionsProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmOpen(true);
  };

  const handleConfirmDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmOpen(false);
    if (onDelete) {
      onDelete();
    }
  };

  const handleCancelDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmOpen(false);
  };

  return (
    <>
      <div className={`inline-flex items-center justify-end ${className}`} onClick={(e) => e.stopPropagation()}>
        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="size-8 rounded-lg border border-transparent hover:border-border hover:bg-muted/60 text-muted-foreground hover:text-foreground flex items-center justify-center transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              title="More actions"
              aria-label="More actions"
            >
              <MoreVertical className="size-4 shrink-0" />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-48 z-50">
            {onViewDetails && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownOpen(false);
                  onViewDetails();
                }}
              >
                <Eye className="size-4 mr-2 text-muted-foreground" />
                <span>View details</span>
              </DropdownMenuItem>
            )}

            {!readOnly && onEdit && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownOpen(false);
                  onEdit();
                }}
              >
                <Edit3 className="size-4 mr-2 text-muted-foreground" />
                <span>Edit</span>
              </DropdownMenuItem>
            )}

            {!readOnly && onDuplicate && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownOpen(false);
                  onDuplicate();
                }}
              >
                <Copy className="size-4 mr-2 text-muted-foreground" />
                <span>Duplicate</span>
              </DropdownMenuItem>
            )}

            {!readOnly && onToggleStatus && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownOpen(false);
                  onToggleStatus();
                }}
              >
                <Power className={`size-4 mr-2 ${isStatusActive ? 'text-amber-500' : 'text-emerald-500'}`} />
                <span>{statusToggleLabel || (isStatusActive ? 'Deactivate' : 'Activate')}</span>
              </DropdownMenuItem>
            )}

            {customActions.map((action, idx) => (
              <DropdownMenuItem
                key={idx}
                disabled={action.disabled}
                destructive={action.destructive}
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownOpen(false);
                  action.onClick();
                }}
              >
                {action.icon && <span className="mr-2">{action.icon}</span>}
                <span>{action.label}</span>
              </DropdownMenuItem>
            ))}

            {!readOnly && onDelete && (
              <>
                {(onViewDetails || onEdit || onDuplicate || onToggleStatus || customActions.length > 0) && (
                  <DropdownMenuSeparator />
                )}
                <DropdownMenuItem
                  destructive
                  onClick={handleDeleteClick}
                >
                  <Trash2 className="size-4 mr-2" />
                  <span>Delete</span>
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Confirmation Dialog */}
      {confirmOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-in fade-in"
          onClick={handleCancelDelete}
        >
          <div 
            className="w-full max-w-md bg-card border border-border rounded-2xl p-6 shadow-xl space-y-4 animate-in zoom-in-95"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive flex items-center justify-center shrink-0">
                <AlertTriangle className="size-5" />
              </div>
              <div>
                <h3 className="font-semibold text-base text-foreground">{deleteTitle}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Please confirm this action</p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground leading-relaxed">
              {deleteMessage || `Are you sure you want to delete ${entityName}? This action cannot be undone.`}
            </p>

            <div className="flex items-center justify-end gap-2.5 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelDelete}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleConfirmDelete}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
